import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, RotateCcw, Volume2, Loader2, VolumeX, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AudioPlayer = ({ questionId, onPlayStart, onPlayEnd, unlimitedPlays = false }) => {
  const [audioSrc, setAudioSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playCount, setPlayCount] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [usingSpeechSynth, setUsingSpeechSynth] = useState(false);
  // v6.38.2 — set to true when the user clicks Play before the audio has
  // finished loading; a useEffect watches audioRef + this flag and starts
  // playback the instant the <audio> element mounts. Fixes the two-click
  // bug where the first click loaded audio but couldn't play (ref was
  // null); the second click succeeded because the element had rendered.
  const [autoplayPending, setAutoplayPending] = useState(false);
  const audioRef = useRef(null);
  const synthUtteranceRef = useRef(null);

  // v6.32.114 — RA accommodation: students with WIDA "Repeat Audio" tag get
  // unlimited replays. Default exam UX caps replays at 2.
  const maxPlays = unlimitedPlays ? Infinity : 2;

  useEffect(() => {
    setAudioSrc(null);
    setIsPlaying(false);
    setProgress(0);
    setPlayCount(0);
    setError(null);
    setShowTranscript(false);
    setTranscript('');
    setUsingSpeechSynth(false);
    setAutoplayPending(false);
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, [questionId]);

  const loadAudio = async () => {
    if (audioSrc) return true;
    
    setLoading(true);
    setError(null);
    
    // v6.36.x pattern: attach Bearer explicitly and disable withCredentials
    // so we don't trip prod's `Access-Control-Allow-Origin: *` + credentials
    // CORS block. Guests (no token) fall through to the public demo endpoint.
    const token = (typeof window !== 'undefined' && window.localStorage)
      ? (localStorage.getItem('mosaic_session_token') || '')
      : '';
    const authedUrl = `${API}/audio/question/${questionId}`;
    const publicUrl = `${API}/audio/demo-question/${questionId}`;
    const primaryUrl = token ? authedUrl : publicUrl;
    const fallbackUrl = token ? publicUrl : null;
    const cfg = {
      timeout: 15000,
      withCredentials: false,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    };

    try {
      let response;
      try {
        response = await axios.get(primaryUrl, cfg);
      } catch (primaryErr) {
        if (fallbackUrl && primaryErr?.response?.status === 401) {
          response = await axios.get(fallbackUrl, { timeout: 15000, withCredentials: false });
        } else {
          throw primaryErr;
        }
      }
      const audioData = response.data.audio;
      const transcriptText = response.data.transcript || '';
      setAudioSrc(`data:audio/mp3;base64,${audioData}`);
      if (transcriptText) setTranscript(transcriptText);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Error loading audio:', err);
      setLoading(false);
      
      // Try to get transcript for fallback
      try {
        const qRes = await axios.get(`${API}/questions?include_standards=true`);
        const questions = qRes.data.questions || [];
        const q = questions.find(q => q.id === questionId);
        if (q) {
          const fallbackText = `${q.stimulus || ''} ${q.question_text || ''}`.trim();
          setTranscript(fallbackText);
        }
      } catch (e) { /* ignore */ }
      
      setError('Audio failed to load.');
      return false;
    }
  };

  const handleBrowserTTS = () => {
    if (!window.speechSynthesis || !transcript) return;
    
    if (playCount >= maxPlays) return;
    
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(transcript);
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.lang = 'en-US';
    
    utterance.onstart = () => {
      setIsPlaying(true);
      setUsingSpeechSynth(true);
      setPlayCount(prev => prev + 1);
      onPlayStart?.();
    };
    utterance.onend = () => {
      setIsPlaying(false);
      setUsingSpeechSynth(false);
      setProgress(100);
      onPlayEnd?.();
    };
    utterance.onerror = () => {
      setIsPlaying(false);
      setUsingSpeechSynth(false);
    };
    
    synthUtteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const handlePlay = async () => {
    if (playCount >= maxPlays) return;

    if (!audioSrc) {
      // First play: queue autoplay so the useEffect below fires the
      // instant the <audio> element mounts (avoids the two-click bug).
      setAutoplayPending(true);
      const loaded = await loadAudio();
      if (!loaded) {
        setAutoplayPending(false);
        // Fallback: use browser TTS
        if (transcript && window.speechSynthesis) {
          handleBrowserTTS();
        }
      }
      return;
    }

    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.currentTime = 0;
        try {
          await audioRef.current.play();
          setIsPlaying(true);
          setPlayCount(prev => prev + 1);
          onPlayStart?.();
        } catch (e) {
          // Play failed, use browser fallback
          if (transcript && window.speechSynthesis) {
            handleBrowserTTS();
          } else {
            setError('Playback failed. View transcript below.');
            setShowTranscript(true);
          }
        }
      }
    }
  };

  // v6.38.2 — fires when audioSrc becomes non-null AND the user
  // pre-clicked Play. Runs after React mounts the <audio> element so
  // the ref is guaranteed to exist.
  useEffect(() => {
    if (autoplayPending && audioSrc && audioRef.current) {
      setAutoplayPending(false);
      audioRef.current.currentTime = 0;
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
          setPlayCount(prev => prev + 1);
          onPlayStart?.();
        })
        .catch(() => {
          // Autoplay blocked or codec failed — fall back to browser TTS
          if (transcript && window.speechSynthesis) handleBrowserTTS();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSrc, autoplayPending]);

  const handleReplay = async () => {
    if (playCount >= maxPlays) return;
    
    if (usingSpeechSynth || (!audioSrc && transcript)) {
      handleBrowserTTS();
      return;
    }
    
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        setPlayCount(prev => prev + 1);
        onPlayStart?.();
      } catch (e) {
        if (transcript && window.speechSynthesis) {
          handleBrowserTTS();
        }
      }
    }
  };

  const handleStop = () => {
    if (usingSpeechSynth) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      setUsingSpeechSynth(false);
    } else if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(100);
    onPlayEnd?.();
  };

  const handleAudioError = () => {
    setError('Audio playback error. Use "Read Aloud" or view transcript.');
    setIsPlaying(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const remainingPlays = maxPlays - playCount;
  const hasBrowserTTS = typeof window !== 'undefined' && window.speechSynthesis;

  return (
    <div className="bg-purple-50 border-2 border-purple-200 p-6 mb-6" data-testid="audio-player">
      <div className="flex items-center gap-2 mb-4">
        <Volume2 className="text-purple-600" size={20} />
        <span className="font-semibold text-purple-800">Listening Audio</span>
        {usingSpeechSynth && (
          <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 font-medium" data-testid="browser-tts-badge">
            Browser Read Aloud
          </span>
        )}
        <span className="text-sm text-purple-600 ml-auto" data-testid="audio-plays-remaining">
          {unlimitedPlays
            ? 'Unlimited plays (RA accommodation)'
            : remainingPlays > 0 ? `${remainingPlays} play${remainingPlays !== 1 ? 's' : ''} remaining` : 'No plays remaining'}
        </span>
      </div>
      
      {/* Error with fallback options */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200" data-testid="audio-error">
          <div className="flex items-center gap-2 text-red-600 text-sm mb-2">
            <VolumeX size={16} />
            <span>{error}</span>
          </div>
          <div className="flex items-center gap-2">
            {hasBrowserTTS && transcript && remainingPlays > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={handleBrowserTTS}
                data-testid="browser-tts-fallback-btn"
              >
                <Volume2 size={14} className="mr-1" /> Read Aloud (Browser)
              </Button>
            )}
            {transcript && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => setShowTranscript(!showTranscript)}
                data-testid="show-transcript-btn"
              >
                <MessageSquare size={14} className="mr-1" /> {showTranscript ? 'Hide' : 'Show'} Transcript
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => { setError(null); setAudioSrc(null); loadAudio(); }}
              data-testid="retry-audio-btn"
            >
              <RotateCcw size={14} className="mr-1" /> Retry
            </Button>
          </div>
        </div>
      )}
      
      {/* Transcript fallback */}
      {showTranscript && transcript && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-sm text-gray-700" data-testid="audio-transcript">
          <div className="text-xs font-semibold text-blue-600 uppercase mb-1">Audio Transcript</div>
          {transcript}
        </div>
      )}
      
      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onError={handleAudioError}
          preload="auto"
        />
      )}
      
      {/* Progress Bar */}
      <div className="h-2 bg-purple-100 mb-4 overflow-hidden">
        <div 
          className="h-full bg-purple-500 transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>
      
      {/* Controls */}
      <div className="flex items-center gap-4">
        <Button
          onClick={isPlaying ? handleStop : handlePlay}
          disabled={loading || (playCount >= maxPlays && !isPlaying)}
          className={`btn-sharp h-12 px-6 ${
            isPlaying 
              ? 'bg-purple-600 text-white hover:bg-purple-700' 
              : 'btn-sharp-primary'
          }`}
          data-testid="play-audio-btn"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin mr-2" size={18} />
              Loading...
            </>
          ) : isPlaying ? (
            <>
              <Pause size={18} className="mr-2" />
              Pause
            </>
          ) : (
            <>
              <Play size={18} className="mr-2" />
              {audioSrc || usingSpeechSynth ? 'Play Again' : 'Play Audio'}
            </>
          )}
        </Button>
        
        {(audioSrc || transcript) && !isPlaying && playCount > 0 && playCount < maxPlays && (
          <Button
            onClick={handleReplay}
            variant="outline"
            className="btn-sharp h-12"
            data-testid="replay-audio-btn"
          >
            <RotateCcw size={18} className="mr-2" />
            Replay
          </Button>
        )}
        
        {duration > 0 && !usingSpeechSynth && (
          <span className="text-sm text-gray-500 ml-auto">
            {formatTime(audioRef.current?.currentTime || 0)} / {formatTime(duration)}
          </span>
        )}
      </div>
      
      <p className="text-xs text-purple-600 mt-4">
        {unlimitedPlays
          ? 'Listen carefully. You may play the audio as many times as you need (RA accommodation).'
          : `Listen carefully. You may play the audio up to ${maxPlays} times.`}
      </p>
    </div>
  );
};

export default AudioPlayer;
